import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { App } from '@/App';
import { queryClient } from '@/lib/query-client';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { ToastProvider } from '@/components/ui/toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { SUPABASE_CONFIGURED } from '@/lib/supabase';
import '@/index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root element missing from index.html');

// Without VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY the entire app is
// non-functional — every query 401s and the login form fails confusingly.
// Short-circuit the React render and tell the dev what to fix.
if (!SUPABASE_CONFIGURED) {
  rootEl.innerHTML = `
    <div style="
      min-height:100vh;display:flex;align-items:center;justify-content:center;
      background:#0b0f17;color:#e6e8ec;font-family:ui-sans-serif,system-ui,sans-serif;padding:1rem;">
      <div style="max-width:30rem;border:1px solid #ef4444;border-radius:.5rem;padding:1.25rem;background:rgba(239,68,68,.08);">
        <div style="font-weight:600;margin-bottom:.5rem;">Supabase env vars missing</div>
        <p style="font-size:.85rem;line-height:1.5;color:#b6bdc7;margin:0 0 .75rem;">
          The Admin app cannot start without <code>VITE_SUPABASE_URL</code> and
          <code>VITE_SUPABASE_ANON_KEY</code>. Copy
          <code>.env.example</code> to <code>.env</code> in
          <code>src/RevoDataPlatform.Admin</code> and fill in your Sports API Data Supabase
          credentials, then restart the dev server.
        </p>
        <p style="font-size:.75rem;color:#6b7280;margin:0;">
          Source: <code>src/lib/supabase.ts</code> · See README for the full local-dev checklist.
        </p>
      </div>
    </div>`;
} else {
  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <ToastProvider>
                <App />
              </ToastProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}
